import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { RangeService } from '../shared/services/range.service';
import { ApiClientService } from '../shared/services/api-client.service';
import { Translated } from '../shared/classes/translated.class';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import { RouterHelper } from '../shared/helpers/router.helper';
import { FilterSetting, LampUpdateResponse } from '../shared/models/common';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageHelper } from '../shared/helpers/localhost.helper';
import { SessionService } from '../shared/services/session.service';
import { MatDialog } from '@angular/material/dialog';
import { AreYouSureDialogComponent } from '../universal-components/are-you-sure-dialog/are-you-sure-dialog.component';

interface ProcessResponse {
  response?: string;
  error?: string;
}

interface ProcessItem {
  id: number;
  response?: string;
  error?: string;
  status: 'pending' | 'success' | 'error';
}

@Component({
  selector: 'app-process',
  templateUrl: './process.component.html',
  styleUrls: ['./process.component.less']
})
export class ProcessComponent extends Translated implements OnInit {
  description: string = '';
  progress: number = 0;
  currentIndex: number = 0;
  totalRecords: number = 0;
  successfulCount: number = 0;
  failureCount: number = 0;
  currentResponse: string = '';
  currentError: string = '';
  isProcessing: boolean = false;
  isComplete: boolean = false;
  isAborted: boolean = false;

  displayedColumns: string[] = ['id', 'status', 'response', 'error'];
  processItems: ProcessItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private rangeService: RangeService,
    private apiClientService: ApiClientService,
    protected translateService: TranslateService,
    protected localStorageHelper: LocalStorageHelper,
    protected sessionService: SessionService,
    private dialog: MatDialog
  ) {
    super(translateService, localStorageHelper, sessionService);
  }

  initThisSubtypeFromSession(): void {
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const url = decodeURIComponent(params['url']);
      const verb = params['verb'] || 'POST';
      this.description = params['description'];
      const rangeId = params['rangeId'];
      const tableName = params['tableName'];
      const maxCount = params['maxCount'];

      this.showConfirmationDialog(() => {
        this.startProcess(url, verb, rangeId, tableName, maxCount);
      });
    });
  }

  private showConfirmationDialog(onConfirm: () => void) {
    const dialogRef = this.dialog.open(AreYouSureDialogComponent, {
      data: {
        title: 'Start process',
        description: this.description,
        subDescription: 'Are you sure?',
        btnYesText: 'Yes',
        btnNoText: 'No'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        onConfirm();
      }
    });
  }

  abortProcess() {
    this.isAborted = true;
    this.isProcessing = false;
    this.isComplete = true;
  }

  private async startProcess(url: string, verb: string, rangeId: string, tableName: string, maxCount?: string) {
    try {
      // Set default values
      verb = verb || 'POST';

      if (!rangeId || !tableName || !maxCount) {
        throw new Error(`Missing required parameters: rangeId=${rangeId}, tableName=${tableName}, maxCount=${maxCount}`);
      }

      // Get list of IDs
      const idsResponse = await this.rangeService.getIds(+rangeId, tableName, +maxCount);
      const ids = idsResponse as number[];
      this.totalRecords = ids.length;
      this.isProcessing = true;
      this.isAborted = false;

      // Initialize process items
      this.processItems = ids.map(id => ({
        id,
        status: 'pending'
      } as ProcessItem));

      // Process each ID
      for (let i = 0; i < ids.length; i++) {
        if (this.isAborted) {
          break;
        }

        try {
          const apiUrl = this.apiClientService.generateUrl(url.replace('{0}', ids[i].toString()));
          const authToken = this.apiClientService.getToken();
          const headers = {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          };
          const response = await this.http[verb.toLowerCase()](apiUrl, {}, { headers }).toPromise() as ProcessResponse;
          if (response.response) {
            this.currentResponse = response.response;
            this.successfulCount++;
            this.processItems[i].status = 'success';
            this.processItems[i].response = response.response;
          }
          if (response.error) {
            this.currentError = response.error;
            this.failureCount++;
            this.processItems[i].status = 'error';
            this.processItems[i].error = response.error;
          }
        } catch (error) {
          this.currentError = error.message;
          this.failureCount++;
          this.processItems[i].status = 'error';
          this.processItems[i].error = error.message;
        } finally {
          this.currentIndex = i + 1;
          this.progress = (this.currentIndex / this.totalRecords) * 100;
        }

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      this.isProcessing = false;
      this.isComplete = true;
      this.progress = 0;
    } catch (error) {
      this.currentError = error.message;
      this.isProcessing = false;
      this.isComplete = true;
    }
  }
} 